/*
* useTrackingSubSectionInView is a custom hook that takes in a current pathname of a page and returns the subSection that is currently in view
* in order to highlight the right nav bar.
*  */

import Section from '../types/RightNavItem';
import { useEffect, useState } from 'react';
import TableContents from '../types/TableContents';

interface SectionItem
{
    inView: boolean;
    section: Section;
    element: HTMLElement;
    height: number;
}

const useTrackingRightNavInView = ( currentURL: string, tableContents: TableContents | undefined ): ( Section | undefined )[] =>
{
    const [ sectionItems, setSectionItems ] = useState<Array<SectionItem>>( [] );

    useEffect( () =>
    {
        // Register trackSectionInView event for every 100ms
        const timer: number = window.setInterval( () => trackSectionCurrentlyInView(), 100 );
        // To avoid memory leak, we need to have a cleanup function.
        return function cleaUp()
        {
            window.clearInterval( timer );
        }
    }, [ currentURL ] );

    // Spy update the state of SectionItems every 100ms to see which element is in view.
    const trackSectionCurrentlyInView = (): void =>
    {
        let sectionsInCurrentPage: Array<SectionItem> = [];

        // Each sectionItem correspond to a HTMLElement from subSection we define, getSubSectionElements will
        // determine which subSection is currently in view and return an array of sectionItems.
        if ( tableContents && tableContents.rightMenuNavs )
        {
            sectionsInCurrentPage = getSubSectionElements( tableContents.rightMenuNavs );
        }

        // There might be 2 or 3 sections in view at the same time, we want the first one.
        const firstTrueSectionInView: SectionItem | undefined = sectionsInCurrentPage.find( item => !!item && item.inView );

        if ( firstTrueSectionInView )
        {
            const updateSectionItems = sectionsInCurrentPage.map( sectionItem =>
            {
                return { ...sectionItem, inView: sectionItem === firstTrueSectionInView } as SectionItem;
            } );
            setSectionItems( updateSectionItems );
        }
        else
        {
            return;
        }
    }

    const getSubSectionElements = ( sections: Array<Section> ) =>
    {
        const result: Array<SectionItem> = [];

        for ( let i = 0; i < sections.length; i++ )
        {
            const section = sections[ i ];
            const element = document.getElementById( decodeURI( 'link-' + section.hashName.slice( 1 ) ) );

            let nextElement: HTMLElement | null;

            if ( i < sections.length - 1 )
            {
                nextElement = document.getElementById( decodeURI( 'link-' + sections[ i + 1 ].hashName.slice( 1 ) ) );
            }
            else
            {
                nextElement = null;
            }


            if ( element )
            {
                const item: SectionItem = {
                    inView: false,
                    section: section,
                    height: nextElement && nextElement.getBoundingClientRect().top - element.getBoundingClientRect().top || 0,
                    element
                }

                result.push( item );
            }
        }

        result.map(
            ( section ) =>
            {
                section.inView = isInView( section );
            }
        )

        return result;
    };

    const isInView = ( section: SectionItem, offsetTop = 120 ): boolean =>
    {
        const sectionHeight = section.height === 0 ? 5000 : section.height;
        const topPosition = section.element.getBoundingClientRect().top;
        const bottomPosition = topPosition + sectionHeight;

        return ( topPosition >= offsetTop || bottomPosition >= window.innerHeight - offsetTop );

    };

    const getSubSectionInView = ( sections: Array<SectionItem> ): Section | undefined =>
    {
        const sectionInView = sections.find( item => !!item && item.inView );
        if ( sectionInView )
        {
            return sectionInView.section;
        }
        else
        {
            return;
        }
    }

    return [ getSubSectionInView( sectionItems ) ];
}

export default useTrackingRightNavInView;
